import React from "react";
import { useLocation, Link } from "react-router-dom";

interface LocationState {
  testData: any; // same structure as generatedTest
  config: {
    difficulty: string;
    duration: number;
    questionCount: number;
  };
  jobTitle: string;
}

const TestPreview: React.FC = () => {
  const location = useLocation();
  const state = location.state as LocationState | undefined;

  if (!state) {
    return (
      <div className="text-center py-10">
        Nothing to preview.
        <div className="mt-4">
          <Link
            to="/hr/test-generator"
            className="text-red-700 hover:underline"
          >
            Back to Generator
          </Link>
        </div>
      </div>
    );
  }

  const { testData, config, jobTitle } = state;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">
          {testData?.title || "Test Preview"}
        </h1>
        <Link
          to="/hr/test-generator"
          className="text-red-700 hover:underline text-sm"
        >
          ← Back to Generator
        </Link>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4 shadow-sm">
        <p className="text-gray-700">
          <strong>Position:</strong> {jobTitle}
        </p>
        <p className="text-gray-700">
          <strong>Difficulty:</strong> {config.difficulty}
        </p>
        <p className="text-gray-700">
          <strong>Duration:</strong> {config.duration} min
        </p>
        <p className="text-gray-700">
          <strong>Total Questions:</strong> {config.questionCount}
        </p>
      </div>

      {/* Sections */}
      {testData.sections?.length ? (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-gray-900">Sections</h2>
          {testData.sections.map((section: any, idx: number) => (
            <div
              key={idx}
              className="border border-gray-200 rounded-lg p-4 flex justify-between"
            >
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  {section.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {section.questions} questions • {section.timeLimit} min
                </p>
              </div>
              <div className="flex flex-wrap gap-2 self-center">
                {(section.skills || []).map((skill: string, i: number) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Questions */}
      {testData.sampleQuestions?.length ? (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-gray-900">Questions</h2>
          {testData.sampleQuestions.map((q: any, idx: number) => (
            <div
              key={q.id}
              className="border border-gray-200 rounded-lg p-6 space-y-3"
            >
              <div className="flex items-center space-x-3">
                <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                  Q{idx + 1}
                </span>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                  {q.skill}
                </span>
                <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full capitalize">
                  {q.difficulty}
                </span>
              </div>
              <h4 className="text-gray-900 font-medium">{q.question}</h4>
              <div className="space-y-2">
                {(q.options || []).map((opt: string, optIdx: number) => (
                  <div
                    key={optIdx}
                    className={`p-3 rounded-lg border ${
                      optIdx + 1 === q.correctAnswer
                        ? "border-green-300 bg-green-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <span className="text-sm">
                      {String.fromCharCode(65 + optIdx)}. {opt}
                    </span>
                    {optIdx + 1 === q.correctAnswer && (
                      <span className="ml-2 text-green-600 text-xs">
                        ✓ Correct
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TestPreview;
